
import { useState, useEffect } from "react";
import { ServiceType, ServiceProvider, PaymentMethodType } from "@/types/booking";
import { getRandomAffirmation } from "@/utils/affirmations";
import { generateUniqueId } from "@/utils/bookingUtils";

export function useBookingProcess() {
  const [step, setStep] = useState(1);
  const [opacity, setOpacity] = useState(1);
  const [translateY, setTranslateY] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [affirmationMessage, setAffirmationMessage] = useState("");
  const [showAffirmation, setShowAffirmation] = useState(false);
  const [bookingData, setBookingData] = useState({
    service: null as ServiceType | null,
    date: null as Date | null,
    time: null as string | null,
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    paymentComplete: false,
    isRecurring: false,
    lateArrivalPolicy: "grace-period" as "grace-period" | "reschedule" | "charge",
    noShowFee: 25,
    walkInAllowed: true,
    serviceProvider: null as ServiceProvider | null,
    welcomeGif: null as string | null,
    paymentMethod: undefined as PaymentMethodType | undefined,
    qrCodeUrl: null as string | null,
    bookingId: null as string | null,
    confirmationNumber: null as string | null,
  });

  const updateBookingData = (data: Partial<typeof bookingData>) => {
    setBookingData((prev) => ({ ...prev, ...data }));
  };

  const showAffirmationToast = (type: keyof typeof import("@/utils/affirmations").bookingAffirmations) => {
    const message = getRandomAffirmation(type);
    setAffirmationMessage(message);
    setShowAffirmation(true);
    
    setTimeout(() => {
      setShowAffirmation(false);
    }, 3000);
  };

  // Animation functions
  const animateOut = () => {
    setIsAnimating(true);
    setOpacity(0);
    setTranslateY(20);
  };

  const animateIn = () => {
    setTimeout(() => {
      setOpacity(1);
      setTranslateY(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }, 50);
  };

  const nextStep = () => {
    animateOut();
    
    setTimeout(() => {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      
      // Generate Stripe QR code URL for in-person payments when moving to confirmation step
      if (step === 3 && bookingData.paymentMethod === "in_person") {
        // In a real application, this would be a call to your backend to create a Stripe payment link
        // For this demo, we'll use a placeholder Stripe QR code URL
        const stripeQrCodeUrl = "https://api.stripe.com/v1/payment_links/plink_1NpEQIJwXEcQ7JasFGUvj5Bj/qr_codes/pqrc_1NpEQIJwXEcQ7JasDQ7dnV4y";
        
        updateBookingData({ qrCodeUrl: stripeQrCodeUrl });
      }
      
      animateIn();
    }, 300);
  };

  const prevStep = () => {
    animateOut();
    
    setTimeout(() => {
      setStep(step - 1);
      animateIn();
    }, 300);
  };

  const goToStep = (targetStep: number) => {
    animateOut();
    
    setTimeout(() => {
      setStep(targetStep);
      window.scrollTo({ top: 0, behavior: "smooth" });
      
      // Generate Stripe QR code URL for in-person payments when moving to confirmation step
      if (targetStep === 4 && bookingData.paymentMethod === "in_person") {
        // In a real application, this would be a call to your backend to create a Stripe payment link
        // For this demo, we'll use a placeholder Stripe QR code URL
        const stripeQrCodeUrl = "https://api.stripe.com/v1/payment_links/plink_1NpEQIJwXEcQ7JasFGUvj5Bj/qr_codes/pqrc_1NpEQIJwXEcQ7JasDQ7dnV4y";
        
        updateBookingData({ qrCodeUrl: stripeQrCodeUrl });
      }
      
      animateIn();
    }, 300);
  };

  // Reset animation state when component mounts
  useEffect(() => {
    setOpacity(1);
    setTranslateY(0);
    setIsAnimating(false);
  }, []);

  return {
    step,
    bookingData,
    affirmationMessage,
    showAffirmation,
    updateBookingData,
    showAffirmationToast,
    nextStep,
    prevStep,
    goToStep,
    setShowAffirmation,
    // Animation properties
    opacity,
    translateY,
    isAnimating
  };
}
